// src/views/admin/caisse/ADCaisseSituationVersements.jsx
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAdminService } from "../../../services/adminService";
import { Loader } from "../../../components/Loader";
import { useToastHook } from "../../../utils/useToast";
import { usePermissions } from "../../../context/PermissionsContext";
import { Modal, AnnulationModal } from "./ADCaisseShared";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const STATUT_SITUATION = {
  A_JOUR: { label: "À jour", pill: "ok" },
  PARTIEL: { label: "Versement partiel", pill: "warn" },
  AUCUN_VERSEMENT: { label: "Aucun versement", pill: "err" },
};

/* ============================================================================
 * STYLES DE CELLULES (tableau de la liste des élèves)
 *
 * Règle : on ne coupe JAMAIS un mot au milieu (ex. « SAWADOG / O »). Le
 * retour à la ligne ne se fait qu'aux espaces et aux tirets.
 *
 * Pourquoi l'ancienne version coupait les mots :
 *  - `overflow-wrap: anywhere` autorise la coupure entre n'importe quelles
 *    lettres ET réduit la largeur minimale de la colonne à un seul caractère.
 *    Le navigateur rétrécissait donc la colonne « Nom » au profit des
 *    colonnes en `nowrap`, puis cassait le mot lettre par lettre.
 *  - `word-break: break-word` produisait le même effet.
 *
 * Maintenant :
 *  - `word-break: normal` + `overflow-wrap: break-word` : coupure aux
 *    espaces uniquement. `break-word` n'influence pas la largeur minimale :
 *    le mot le plus long fixe la largeur minimale de la colonne.
 *  - `min-width` garantit une largeur confortable pour un nom.
 *  - le tableau a une `min-width` globale : si l'écran est trop étroit, le
 *    conteneur défile horizontalement au lieu d'écraser les colonnes.
 *  - `hyphens: none` empêche les césures automatiques.
 * ========================================================================== */
const CELL_TEXT = {
  whiteSpace: "normal",
  wordBreak: "normal",
  overflowWrap: "break-word",
  hyphens: "none",
  overflow: "visible",
  textOverflow: "clip",
};
const CELL_NOM = { ...CELL_TEXT, minWidth: 140, fontWeight: 500 };
const CELL_PRENOM = { ...CELL_TEXT, minWidth: 150 };
const CELL_TYPE = { ...CELL_TEXT, minWidth: 110, maxWidth: 180 };
const CELL_NOWRAP = { whiteSpace: "nowrap" };

/* ============================================================================
 * PERMISSIONS (module "caisse") utilisées dans ce fichier — même schéma que
 * ADEleves.jsx :
 * - caisse.voir_situation      : accès à cet onglet dans son ensemble
 *                                (déjà filtré en amont dans ADCaisse.jsx —
 *                                le composant reste défensif au cas où il
 *                                serait rendu directement)
 * - caisse.exporter_situation  : boutons "Télécharger PDF" / "Imprimer"
 *                                (liste ET relevé d'un élève)
 * - caisse.encaisser_versement : bouton "Nouveau versement"
 * - caisse.annuler_versement   : bouton "Annuler" sur un versement de
 *                                l'historique
 * - caisse.gerer_remises       : bouton "Nouvelle remise"
 * ========================================================================== */

/* ============================================================================
 * UTILITAIRES
 * ========================================================================== */

// Number(x).toLocaleString() insère une espace fine insécable (U+202F) comme
// séparateur de milliers, non supportée par l'encodage des polices standard
// de jsPDF (elle s'affiche comme un "/"). On formate donc les montants
// nous-mêmes avec une espace normale, sans dépendre d'une locale.
const formatMontant = (n) => {
  const val = Math.round(Number(n) || 0);
  const neg = val < 0;
  const digits = Math.abs(val).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return neg ? `-${digits}` : digits;
};

// Échappe les valeurs injectées dans le HTML d'impression (noms, motifs...).
const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/* ============================================================================
 * BOUTONS D'ACTION — même design que les autres onglets Caisse (couleur
 * pleine par action + hover JS opacity 0.8).
 * ========================================================================== */
const ActionBtn = ({ title, icon, onClick, bg, color = "white", disabled = false }) => (
  <button
    title={title}
    onClick={onClick}
    disabled={disabled}
    style={{ background: bg, color, opacity: disabled ? 0.5 : 1 }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = "0.8"; }}
    onMouseLeave={e => { e.currentTarget.style.opacity = disabled ? "0.5" : "1"; }}
  >
    <i className={`ti ${icon}`} />
  </button>
);

/* ============================================================================
 * PAGINATION CÔTÉ CLIENT
 * La liste est entièrement chargée (une situation par élève), puis filtrée et
 * découpée avec slice(). Les totaux, le PDF et l'impression portent toujours
 * sur TOUTE la liste filtrée, pas seulement sur la page affichée.
 * ========================================================================== */
const paginationBtn = (isActive = false, isDisabled = false) => ({
  minWidth: "32px",
  height: "32px",
  padding: "0 8px",
  border: "1px solid",
  borderColor: isActive ? "#4a90e2" : "#dee2e6",
  borderRadius: "6px",
  background: isActive ? "#4a90e2" : "#ffffff",
  color: isActive ? "#ffffff" : isDisabled ? "#adb5bd" : "#495057",
  cursor: isDisabled ? "not-allowed" : "pointer",
  fontSize: "13px",
  fontWeight: isActive ? "600" : "400",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s",
  opacity: isDisabled ? 0.6 : 1,
});

const getPageNumbersFor = (current, total) => {
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  range.forEach((i) => {
    if (l) {
      if (i - l === 2) rangeWithDots.push(l + 1);
      else if (i - l !== 1) rangeWithDots.push("...");
    }
    rangeWithDots.push(i);
    l = i;
  });
  return rangeWithDots;
};

/* ============================================================================
 * SITUATION ÉLÈVE & VERSEMENTS
 * Point d'entrée exporté par défaut, importé par ADCaisse.jsx
 *
 * DEEP-LINK : si l'URL contient ?inscription=<id> (typiquement en arrivant
 * depuis /caisse?tab=situation&inscription=123 après une inscription), on
 * ouvre directement la fiche de situation de cette inscription, sans passer
 * par la liste ni par les filtres année/classe. getSituationFrais() suffit
 * à tout charger depuis l'inscriptionId, pas besoin de résoudre l'élève au
 * préalable.
 *
 * RAFRAÎCHISSEMENT : ce composant reste monté pendant que la fiche d'un élève
 * est affichée. Les situations de la liste (Dû / Payé / Restant / Statut)
 * sont donc rechargées en arrière-plan au retour (retourListe), afin de
 * refléter tout versement, remise ou annulation effectué entre-temps.
 *
 * PDF / Impression : même approche que ADCaisseBilan.jsx (genererPDFListe /
 * imprimerListe pour la vue d'ensemble, genererPDFReleve / imprimerReleve
 * pour la fiche d'un élève) — jsPDF + autoTable pour le PDF, une fenêtre
 * HTML dédiée pour l'impression.
 * ========================================================================== */
export default function SituationVersements({ slug }) {
  const { canSee } = usePermissions();
  const peutVoirSituation = canSee("caisse.voir_situation");
  const peutExporter = canSee("caisse.exporter_situation");
  const peutEncaisser = canSee("caisse.encaisser_versement");
  const peutAnnuler = canSee("caisse.annuler_versement");
  const peutGererRemises = canSee("caisse.gerer_remises");

  const [searchParams, setSearchParams] = useSearchParams();
  const [annees, setAnnees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [typesEleves, setTypesEleves] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [classeFilt, setClasseFilt] = useState(""); // "" = toutes les classes
  const [typeEleveFilt, setTypeEleveFilt] = useState("");
  const [statutFilt, setStatutFilt] = useState("");
  const [eleves, setEleves] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [eleveSelectionne, setEleveSelectionne] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // ─── Pagination (côté client) ──────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Identifiant de la dernière requête de chargement : permet d'ignorer la
  // réponse d'un chargement devenu obsolète (changement rapide de filtre,
  // retour de fiche pendant un chargement, etc.).
  const requestIdRef = useRef(0);

  const toast = useToastHook();
  const { getAnneesScolaires, getClasses, getEleves, getTypesEleves, getSituationFrais } = useAdminService();

  useEffect(() => { if (slug && peutVoirSituation) init(); }, [slug, peutVoirSituation]);
  // On charge dès qu'on a une année sélectionnée — plus besoin d'une classe.
  useEffect(() => { if (slug && anneeFilt) loadEleves(); }, [slug, anneeFilt, classeFilt]);

  // Retour à la page 1 dès qu'un filtre ou la taille de page change.
  useEffect(() => {
    setPage(1);
  }, [search, typeEleveFilt, statutFilt, classeFilt, anneeFilt, pageSize]);

  // ── Deep-link depuis une autre page (ex: après une inscription) ──
  // Si l'URL contient ?inscription=<id>, on ouvre directement la fiche de
  // situation correspondante, en court-circuitant la liste et les filtres.
  useEffect(() => {
    const inscriptionParam = searchParams.get("inscription");
    if (inscriptionParam) {
      setEleveSelectionne({ inscriptionId: inscriptionParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, teData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getTypesEleves(slug),
      ]);
      setAnnees(anneesData);
      setTypesEleves(teData);
      const anneeActive = anneesData.find(a => a.actif);
      const id = String((anneeActive || anneesData[0])?.id || "");
      setAnneeFilt(id);
      if (id) setClasses(await getClasses(slug, { annee_scolaire: id, show_archived: "false" }));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const changerAnnee = async (id) => {
    setAnneeFilt(id);
    setClasseFilt("");
    setEleves([]);
    setEleveSelectionne(null);
    try {
      setClasses(await getClasses(slug, { annee_scolaire: id, show_archived: "false" }));
    } catch (error) {
      toast.error("Impossible de charger les classes");
    }
  };

  /**
   * Charge les élèves + leur situation financière.
   * @param {boolean} silent - true : pas de Loader plein écran (rafraîchissement
   *                           en arrière-plan, la liste actuelle reste visible).
   */
  const loadEleves = async (silent = false) => {
    const requestId = ++requestIdRef.current;
    if (!silent) setLoadingEleves(true);
    try {
      // Avec une classe choisie : filtre côté API (comme avant, plus rapide).
      // Sans classe : on récupère tous les élèves et on filtre par année côté client
      // en résolvant l'inscription correspondant à l'année sélectionnée.
      const params = { page_size: "all" };
      if (classeFilt) params.classe_id = classeFilt;

      const data = await getEleves(slug, params);
      const base = data.results || data || [];

      const enrichis = await Promise.all(base.map(async (e) => {
        const inscription = classeFilt
          ? e.inscriptions?.find(i => String(i.classe) === String(classeFilt))
          : e.inscriptions?.find(i => String(i.annee_scolaire) === String(anneeFilt));

        if (!inscription) return { ...e, inscriptionId: null, situation: null };

        try {
          const situation = await getSituationFrais(slug, inscription.id);
          return {
            ...e,
            inscriptionId: inscription.id,
            classeNom: inscription.classe_nom,
            typeEleveId: inscription.type_eleve,
            typeEleveLibelle: inscription.type_eleve_libelle,
            situation,
          };
        } catch (error) {
          return { ...e, inscriptionId: inscription.id, classeNom: inscription.classe_nom, situation: null };
        }
      }));

      // Une requête plus récente a démarré entre-temps : on abandonne ce résultat.
      if (requestId !== requestIdRef.current) return;

      // On ignore les élèves sans inscription pour l'année/classe filtrée
      // (ex: élève inscrit une autre année seulement).
      setEleves(enrichis.filter(e => e.inscriptionId));
    } catch (error) {
      if (requestId === requestIdRef.current) toast.error("Impossible de charger les élèves");
    } finally {
      if (!silent && requestId === requestIdRef.current) setLoadingEleves(false);
    }
  };

  // ─── Filtrage (mémoïsé) ────────────────────────────────────────────────
  const filtres = useMemo(() => {
    const terme = search.trim().toLowerCase();
    return eleves.filter(e => {
      if (terme) {
        const texte = `${e.prenom || ""} ${e.nom || ""} ${e.matricule || ""}`.toLowerCase();
        if (!texte.includes(terme)) return false;
      }

      if (typeEleveFilt && String(e.typeEleveId) !== typeEleveFilt) return false;

      if (statutFilt) {
        const totalPaye = Number(e.situation?.total_paye || 0);
        const statut = e.situation?.statut;
        if (statutFilt === "A_JOUR" && statut !== "A_JOUR") return false;
        if (statutFilt === "NON_A_JOUR" && statut === "A_JOUR") return false;
        if (statutFilt === "AVEC_VERSEMENT" && totalPaye <= 0) return false;
        if (statutFilt === "SANS_VERSEMENT" && totalPaye > 0) return false;
      }
      return true;
    });
  }, [eleves, search, typeEleveFilt, statutFilt]);

  // ─── Découpage de la liste filtrée en pages ────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtres.length / pageSize));
  const pageCourante = Math.min(page, totalPages); // sécurité si la liste rétrécit
  const elevesPage = filtres.slice((pageCourante - 1) * pageSize, pageCourante * pageSize);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const anneeSelectionnee = annees.find(a => String(a.id) === String(anneeFilt));
  const classeSelectionnee = classes.find(c => String(c.id) === String(classeFilt));
  const typeSelectionne = typesEleves.find(te => String(te.id) === String(typeEleveFilt));
  const statutLabel = {
    A_JOUR: "Élèves à jour des frais",
    NON_A_JOUR: "Élèves non à jour des frais",
    AVEC_VERSEMENT: "Ayant effectué au moins un versement",
    SANS_VERSEMENT: "N'ayant effectué aucun versement",
  }[statutFilt];

  const { totalDuListe, totalPayeListe, totalResteListe } = useMemo(() => ({
    totalDuListe: filtres.reduce((s, e) => s + Number(e.situation?.total_du || 0), 0),
    totalPayeListe: filtres.reduce((s, e) => s + Number(e.situation?.total_paye || 0), 0),
    totalResteListe: filtres.reduce((s, e) => s + Number(e.situation?.total_reste || 0), 0),
  }), [filtres]);

  // ─── PDF (jsPDF + autoTable — même pattern que ADCaisseBilan.jsx) ──────
  const genererPDFListe = () => {
    if (filtres.length === 0) return;
    setGeneratingPDF(true);
    try {
      // Paysage : 9 colonnes, plus lisible qu'en portrait.
      const doc = new jsPDF("landscape", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const dateGen = new Date().toLocaleDateString("fr-FR");

      doc.setFontSize(16);
      doc.setTextColor(0, 64, 128);
      doc.setFont(undefined, "bold");
      doc.text("SITUATION DES ÉLÈVES", pageWidth / 2, 16, { align: "center" });

      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "normal");
      const sousTitreParts = [
        `Année : ${anneeSelectionnee?.nom || "—"}`,
        `Classe : ${classeSelectionnee?.nom || "Toutes"}`,
        `Type : ${typeSelectionne?.libelle || "Tous"}`,
      ];
      if (statutLabel) sousTitreParts.push(`Statut : ${statutLabel}`);
      doc.text(sousTitreParts.join("   ·   "), pageWidth / 2, 23, { align: "center" });

      let yMeta = 29;
      if (search) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(`Recherche : "${search}"`, pageWidth / 2, yMeta, { align: "center" });
        yMeta += 5;
      }

      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Généré le ${dateGen}`, pageWidth / 2, yMeta, { align: "center" });

      doc.setDrawColor(0, 64, 128);
      doc.setLineWidth(0.5);
      const yLine = yMeta + 3;
      doc.line(14, yLine, pageWidth - 14, yLine);

      let y = yLine + 7;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Élèves : ${filtres.length}   ·   Dû : ${formatMontant(totalDuListe)} FCFA   ·   Payé : ${formatMontant(totalPayeListe)} FCFA   ·   Reste : ${formatMontant(totalResteListe)} FCFA`,
        14, y
      );
      y += 8;

      autoTable(doc, {
        startY: y,
        head: [["Matricule", "Nom", "Prénom", "Classe", "Type", "Dû", "Payé", "Restant", "Statut"]],
        body: filtres.map(e => {
          const statut = STATUT_SITUATION[e.situation?.statut]?.label || (e.situation ? e.situation.statut : "—");
          return [
            e.matricule || "—",
            e.nom,
            e.prenom,
            e.classeNom || "—",
            e.typeEleveLibelle || "—",
            e.situation ? formatMontant(e.situation.total_du) : "—",
            e.situation ? formatMontant(e.situation.total_paye) : "—",
            e.situation ? formatMontant(e.situation.total_reste) : "—",
            statut,
          ];
        }),
        columnStyles: {
          5: { halign: "right" },
          6: { halign: "right" },
          7: { halign: "right", fontStyle: "bold" },
          8: { halign: "center" },
        },
        theme: "grid",
        styles: { fontSize: 8.5, cellPadding: 3, textColor: [0, 0, 0] },
        headStyles: { fillColor: [0, 64, 128], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
        alternateRowStyles: { fillColor: [240, 246, 255] },
        margin: { left: 14, right: 14, top: 16, bottom: 18 },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: "center" });
      }

      doc.save(`situation-eleves-${dateGen.replace(/\//g, "-")}.pdf`);
      toast.success("PDF généré avec succès");
    } catch (error) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  // ─── Impression (fenêtre HTML dédiée) ──────────────────────────────────
  const imprimerListe = () => {
    if (filtres.length === 0) return;
    const dateGen = new Date().toLocaleDateString("fr-FR");

    const td = (v, align = "left") =>
      `<td style="padding:6px 8px;border:1px solid #ccc;text-align:${align};">${v}</td>`;

    const lignes = filtres.map((e, i) => {
      const statut = STATUT_SITUATION[e.situation?.statut]?.label || (e.situation ? e.situation.statut : "—");
      return `
        <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
          ${td(esc(e.matricule || "—"))}
          ${td(esc(e.nom))}
          ${td(esc(e.prenom))}
          ${td(esc(e.classeNom || "—"))}
          ${td(esc(e.typeEleveLibelle || "—"))}
          ${td(e.situation ? Number(e.situation.total_du).toLocaleString() : "—", "right")}
          ${td(e.situation ? Number(e.situation.total_paye).toLocaleString() : "—", "right")}
          ${td(e.situation ? Number(e.situation.total_reste).toLocaleString() : "—", "right")}
          ${td(esc(statut), "center")}
        </tr>`;
    }).join("");

    const sousTitre = `Année : ${esc(anneeSelectionnee?.nom || "—")} · Classe : ${esc(classeSelectionnee?.nom || "Toutes")} · Type : ${esc(typeSelectionne?.libelle || "Tous")}${statutLabel ? ` · Statut : ${esc(statutLabel)}` : ""}`;

    const html = `
      <!DOCTYPE html>
      <html>
        <head><meta charset="UTF-8"><title>Situation des élèves</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Times New Roman', serif; padding: 20px; background: white; color: #000; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .header { text-align: center; margin-bottom: 18px; border-bottom: 2px solid #004080; padding-bottom: 12px; }
            .header h1 { font-size: 20pt; color: #004080; text-transform: uppercase; margin-bottom: 6px; }
            .header .sous-titre { font-size: 11pt; font-weight: bold; color: #222; margin-bottom: 4px; }
            .header .meta { font-size: 10pt; color: #555; }
            .kpis { font-size: 10.5pt; margin-bottom: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 9.5pt; }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
            thead tr { background: #004080 !important; color: white; }
            thead th { padding: 7px 8px; border: 1px solid #003366; text-align: center; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Situation des élèves</h1>
            <div class="sous-titre">${sousTitre}</div>
            <div class="meta">${search ? `Recherche : "${esc(search)}" &nbsp;·&nbsp; ` : ""}Généré le ${dateGen}</div>
          </div>
          <div class="kpis">
            Élèves : <strong>${filtres.length}</strong> &nbsp;·&nbsp;
            Dû : <strong>${totalDuListe.toLocaleString()} FCFA</strong> &nbsp;·&nbsp;
            Payé : <strong>${totalPayeListe.toLocaleString()} FCFA</strong> &nbsp;·&nbsp;
            Reste : <strong>${totalResteListe.toLocaleString()} FCFA</strong>
          </div>
          <table>
            <thead><tr><th>Matricule</th><th>Nom</th><th>Prénom</th><th>Classe</th><th>Type</th><th>Dû</th><th>Payé</th><th>Restant</th><th>Statut</th></tr></thead>
            <tbody>${lignes}</tbody>
          </table>
          <script>
            window.onload = function() { setTimeout(function() { window.print(); }, 300); };
            window.onafterprint = function() { window.close(); };
          <\/script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=1100,height=750,scrollbars=yes");
    if (!printWindow) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression (pop-up bloquée ?)");
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  };

  const ouvrirSituation = (eleve) => {
    if (!eleve.inscriptionId) {
      toast.error("Aucune inscription trouvée pour cet élève");
      return;
    }
    setEleveSelectionne({ ...eleve, inscriptionId: eleve.inscriptionId, classeNom: eleve.classeNom });
  };

  const retourListe = () => {
    setEleveSelectionne(null);
    // Les montants / statuts ont pu changer (versement, remise, annulation) :
    // on recharge la liste en arrière-plan, sans Loader, pour que les chiffres
    // soient à jour sans devoir rafraîchir la page.
    if (slug && anneeFilt) loadEleves(true);
    // Nettoie le paramètre de deep-link pour éviter de rouvrir la même
    // fiche si l'utilisateur navigue ailleurs puis revient sur cet onglet.
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("inscription");
      return next;
    });
  };

  if (!peutVoirSituation) {
    return (
      <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
        Vous n'avez pas accès à la situation des élèves.
      </div>
    );
  }

  if (loading) return <Loader />;

  if (eleveSelectionne) {
    return (
      <SituationEleve
        slug={slug}
        eleve={eleveSelectionne}
        onBack={retourListe}
        peutExporter={peutExporter}
        peutEncaisser={peutEncaisser}
        peutAnnuler={peutAnnuler}
        peutGererRemises={peutGererRemises}
      />
    );
  }

  return (
    <div>
      <div className="sb2">
        <select value={anneeFilt} onChange={e => changerAnnee(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <select value={classeFilt} onChange={e => setClasseFilt(e.target.value)}>
          <option value="">-- Toutes les classes --</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <select value={typeEleveFilt} onChange={e => setTypeEleveFilt(e.target.value)}>
          <option value="">-- Tous les types d'élèves --</option>
          {typesEleves.map(te => <option key={te.id} value={te.id}>{te.libelle}</option>)}
        </select>
        <select value={statutFilt} onChange={e => setStatutFilt(e.target.value)}>
          <option value="">-- Tous les statuts --</option>
          <option value="A_JOUR">Élèves à jour des frais</option>
          <option value="NON_A_JOUR">Élèves non à jour des frais</option>
          <option value="AVEC_VERSEMENT">Ayant effectué au moins un versement</option>
          <option value="SANS_VERSEMENT">N'ayant effectué aucun versement</option>
        </select>
        <input type="text" placeholder="Rechercher un élève…" value={search} onChange={e => setSearch(e.target.value)} />
        <select
          value={pageSize}
          onChange={e => setPageSize(Number(e.target.value))}
          title="Nombre d'élèves par page"
        >
          <option value="10">10/page</option>
          <option value="20">20/page</option>
          <option value="50">50/page</option>
          <option value="100">100/page</option>
        </select>
        {/* caisse.exporter_situation */}
        {peutExporter && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              className="bsec"
              onClick={genererPDFListe}
              disabled={filtres.length === 0 || generatingPDF}
              style={{ color: "#dc3545", borderColor: "#dc3545" }}
            >
              <i className="ti ti-file-pdf" /> {generatingPDF ? "Génération..." : "Télécharger PDF"}
            </button>
            <button
              className="bsec"
              onClick={imprimerListe}
              disabled={filtres.length === 0}
              style={{ color: "#6f42c1", borderColor: "#6f42c1" }}
            >
              <i className="ti ti-printer" />Imprimer
            </button>
          </div>
        )}
      </div>

      {loadingEleves ? <Loader /> : (
        <>
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            {/* minWidth global : sur petit écran, le conteneur défile
                horizontalement au lieu d'écraser (et couper) les colonnes. */}
            <table className="tbl" style={{ tableLayout: "auto", width: "100%", minWidth: 960 }}>
              <thead>
                <tr>
                  <th style={CELL_NOWRAP}>Matricule</th>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th style={CELL_NOWRAP}>Classe</th>
                  <th>Type</th>
                  <th style={CELL_NOWRAP}>Frais</th>
                  <th style={CELL_NOWRAP}>Payé</th>
                  <th style={CELL_NOWRAP}>Restant</th>
                  <th style={CELL_NOWRAP}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {elevesPage.map(e => {
                  const statut = STATUT_SITUATION[e.situation?.statut] || { label: e.situation ? e.situation.statut : "—", pill: "neu" };
                  return (
                    <tr key={e.id} className="clickable-row" onClick={() => ouvrirSituation(e)}>
                      <td style={CELL_NOWRAP}>{e.matricule || "—"}</td>
                      <td style={CELL_NOM}>{e.nom}</td>
                      <td style={CELL_PRENOM}>{e.prenom}</td>
                      <td style={CELL_NOWRAP}>{e.classeNom || "—"}</td>
                      <td style={CELL_TYPE}>{e.typeEleveLibelle || "—"}</td>
                      <td style={CELL_NOWRAP}>{e.situation ? Number(e.situation.total_du).toLocaleString() : "—"}</td>
                      <td style={CELL_NOWRAP}>{e.situation ? Number(e.situation.total_paye).toLocaleString() : "—"}</td>
                      <td style={{ ...CELL_NOWRAP, color: e.situation && Number(e.situation.total_reste) > 0 ? "var(--rd)" : "var(--gn)", fontWeight: 600 }}>
                        {e.situation ? Number(e.situation.total_reste).toLocaleString() : "—"}
                      </td>
                      <td style={CELL_NOWRAP}><span className={`pill ${statut.pill}`}>{statut.label}</span></td>
                    </tr>
                  );
                })}
                {filtres.length === 0 && (
                  <tr><td colSpan="9" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun élève trouvé</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: "flex", justifyContent: "center", alignItems: "center", gap: "6px",
                marginTop: "20px", flexWrap: "wrap", padding: "16px",
                background: "var(--s2)", borderRadius: "8px",
              }}
            >
              <button onClick={() => handlePageChange(1)} disabled={pageCourante === 1} style={paginationBtn(false, pageCourante === 1)}>«</button>
              <button onClick={() => handlePageChange(pageCourante - 1)} disabled={pageCourante === 1} style={paginationBtn(false, pageCourante === 1)}>‹</button>

              {getPageNumbersFor(pageCourante, totalPages).map((p, index) =>
                p === "..." ? (
                  <span key={`dots-${index}`} style={{ padding: "0 4px", color: "#adb5bd" }}>…</span>
                ) : (
                  <button key={p} onClick={() => handlePageChange(p)} style={paginationBtn(p === pageCourante)}>{p}</button>
                )
              )}

              <button onClick={() => handlePageChange(pageCourante + 1)} disabled={pageCourante === totalPages} style={paginationBtn(false, pageCourante === totalPages)}>›</button>
              <button onClick={() => handlePageChange(totalPages)} disabled={pageCourante === totalPages} style={paginationBtn(false, pageCourante === totalPages)}>»</button>

              <span style={{ marginLeft: "12px", fontSize: "13px", color: "#6c757d" }}>
                Page {pageCourante} / {totalPages} &nbsp;·&nbsp; {filtres.length} élève{filtres.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </>
      )}

      <style>{`
        .clickable-row { cursor: pointer; }
        .clickable-row:hover { background: var(--s2); }
      `}</style>
    </div>
  );
}

function SituationEleve({ slug, eleve, onBack, peutExporter, peutEncaisser, peutAnnuler, peutGererRemises }) {
  const inscriptionId = eleve.inscriptionId; // résolu en amont dans SituationVersements
  const [situation, setSituation] = useState(null);
  const [remises, setRemises] = useState([]);
  const [versements, setVersements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'versement' | 'remise'
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Actions sur un versement de l'historique (imprimer / annuler / voir motif)
  const [busyVersementId, setBusyVersementId] = useState(null);
  const [versementAnnulerCible, setVersementAnnulerCible] = useState(null);
  const [versementMotifCible, setVersementMotifCible] = useState(null);

  const toast = useToastHook();
  const { getSituationFrais, getRemisesEleve, getVersementsHistorique, getVersementRecuPDF, annulerVersement } = useAdminService();

  useEffect(() => { if (slug && inscriptionId) load(); }, [slug, inscriptionId]);

  /**
   * @param {boolean} silent - true : pas de Loader plein écran (rechargement
   *                           après versement / remise / annulation : la fiche
   *                           reste affichée pendant la mise à jour).
   */
  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [sit, rem, vers] = await Promise.all([
        getSituationFrais(slug, inscriptionId),
        getRemisesEleve(slug, inscriptionId).catch(() => []),
        getVersementsHistorique(slug, inscriptionId).catch(() => []),
      ]);
      setSituation(sit);
      setRemises(rem);
      setVersements(vers);
    } catch (error) {
      toast.error("Impossible de charger la situation financière");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleVersementCree = () => {
    setModal(null);
    load(true); // recharge situation + historique réel depuis le backend
  };

  const handleImprimerVersement = async (v) => {
    setBusyVersementId(v.id);
    try {
      const { blob, nombreImpressions } = await getVersementRecuPDF(slug, v.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      if (nombreImpressions > 1) {
        toast.info(`Attention : ceci est la ${nombreImpressions}ᵉ impression de ce reçu (marquée "DUPLICATA" sur le PDF).`);
      }
    } catch (error) {
      toast.error(error.response?.status === 501 ? "Générateur de reçu PDF pas encore implémenté côté backend" : "Impossible de générer le reçu");
    } finally {
      setBusyVersementId(null);
    }
  };

  const handleConfirmerAnnulation = async (motif) => {
    setBusyVersementId(versementAnnulerCible.id);
    try {
      await annulerVersement(slug, versementAnnulerCible.id, motif);
      toast.success("Versement annulé avec succès");
      setVersementAnnulerCible(null);
      load(true);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'annuler ce versement");
    } finally {
      setBusyVersementId(null);
    }
  };

  if (loading || !situation) return <Loader />;
  const statut = STATUT_SITUATION[situation.statut] || { label: situation.statut, pill: "neu" };

  // Total encaissé pour l'historique affiché (versements validés uniquement —
  // un versement annulé ne doit pas compter dans le total en bas du tableau).
  const totalVersements = versements
    .filter(v => v.statut === "VALIDE")
    .reduce((s, v) => s + Number(v.montant), 0);

  // Totaux du détail par tranche (Dû / Payé / Reste), tous types de frais confondus.
  const totalDetailDu = situation.detail.reduce((s, d) => s + Number(d.montant_du), 0);
  const totalDetailPaye = situation.detail.reduce((s, d) => s + Number(d.montant_paye), 0);
  const totalDetailReste = situation.detail.reduce((s, d) => s + Number(d.reste_a_payer), 0);

  // ─── PDF (jsPDF + autoTable — même pattern que ADCaisseBilan.jsx) ──────
  const genererPDFReleve = () => {
    if (!situation) return;
    setGeneratingPDF(true);
    try {
      const doc = new jsPDF("portrait", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const dateGen = new Date().toLocaleDateString("fr-FR");

      doc.setFontSize(16);
      doc.setTextColor(0, 64, 128);
      doc.setFont(undefined, "bold");
      doc.text("RELEVÉ DE SITUATION", pageWidth / 2, 18, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "normal");
      doc.text(situation.eleve, pageWidth / 2, 27, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(situation.classe, pageWidth / 2, 33, { align: "center" });

      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Généré le ${dateGen}`, pageWidth / 2, 39, { align: "center" });

      doc.setDrawColor(0, 64, 128);
      doc.setLineWidth(0.5);
      doc.line(14, 42, pageWidth - 14, 42);

      let y = 49;
      const checkPageBreak = (needed) => {
        if (y + needed > pageHeight - 16) { doc.addPage(); y = 18; }
      };
      const sectionTitle = (titre) => {
        checkPageBreak(12);
        doc.setFontSize(12);
        doc.setTextColor(0, 64, 128);
        doc.setFont(undefined, "bold");
        doc.text(titre, 14, y);
        y += 6;
        doc.setFont(undefined, "normal");
        doc.setTextColor(0, 0, 0);
      };

      doc.setFontSize(10);
      doc.text(`Total dû : ${formatMontant(situation.total_du)} FCFA`, 14, y); y += 5.5;
      doc.text(`Remises : ${formatMontant(situation.total_remises)} FCFA`, 14, y); y += 5.5;
      doc.text(`Total payé : ${formatMontant(situation.total_paye)} FCFA`, 14, y); y += 5.5;
      doc.text(`Reste à payer : ${formatMontant(situation.total_reste)} FCFA`, 14, y); y += 5.5;
      doc.text(`Statut : ${statut.label}`, 14, y); y += 9;

      const tableStyle = {
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3, textColor: [0, 0, 0] },
        headStyles: { fillColor: [0, 64, 128], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
        alternateRowStyles: { fillColor: [240, 246, 255] },
        margin: { left: 14, right: 14, top: 18, bottom: 20 },
      };

      if (versements.length > 0) {
        sectionTitle("Historique des versements");
        autoTable(doc, {
          startY: y,
          head: [["Date", "Reçu", "Montant", "Mode", "Statut"]],
          body: versements.map(v => [
            new Date(v.date_versement).toLocaleDateString(),
            v.numero_recu,
            `${formatMontant(v.montant)} FCFA`,
            v.mode_paiement_libelle || "—",
            v.statut,
          ]),
          columnStyles: { 0: { halign: "center" }, 2: { halign: "right" }, 4: { halign: "center" } },
          ...tableStyle,
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      sectionTitle("Détail par tranche");
      autoTable(doc, {
        startY: y,
        head: [["Type de frais", "Tranche", "Échéance", "Dû", "Payé", "Reste"]],
        body: situation.detail.map(d => [
          d.type_frais, d.tranche, d.date_echeance,
          formatMontant(d.montant_du), formatMontant(d.montant_paye), formatMontant(d.reste_a_payer),
        ]),
        columnStyles: {
          2: { halign: "center" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right", fontStyle: "bold" },
        },
        ...tableStyle,
      });
      y = doc.lastAutoTable.finalY + 10;

      if (remises.length > 0) {
        sectionTitle("Remises accordées");
        autoTable(doc, {
          startY: y,
          head: [["Valeur", "Motif", "Date"]],
          body: remises.map(r => [
            r.montant ? `${formatMontant(r.montant)} FCFA` : `${r.pourcentage}%`,
            r.motif,
            new Date(r.date_creation).toLocaleDateString(),
          ]),
          columnStyles: { 0: { halign: "right" }, 2: { halign: "center" } },
          ...tableStyle,
        });
      }

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: "center" });
      }

      const suffixe = situation.eleve.replace(/\s+/g, "-");
      doc.save(`releve-${suffixe}-${dateGen.replace(/\//g, "-")}.pdf`);
      toast.success("PDF généré avec succès");
    } catch (error) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  // ─── Impression (fenêtre HTML dédiée) ──────────────────────────────────
  const imprimerReleve = () => {
    if (!situation) return;
    const dateGen = new Date().toLocaleDateString("fr-FR");

    const td = (v, align = "left") =>
      `<td style="padding:6px 8px;border:1px solid #ccc;text-align:${align};">${v}</td>`;

    const ligneVersements = versements.map((v, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(new Date(v.date_versement).toLocaleDateString())}
        ${td(esc(v.numero_recu))}
        ${td(`${Number(v.montant).toLocaleString()} FCFA`, "right")}
        ${td(esc(v.mode_paiement_libelle || "—"))}
        ${td(esc(v.statut), "center")}
      </tr>`).join("");

    const ligneDetail = situation.detail.map((d, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(esc(d.type_frais))}
        ${td(esc(d.tranche))}
        ${td(esc(d.date_echeance), "center")}
        ${td(Number(d.montant_du).toLocaleString(), "right")}
        ${td(Number(d.montant_paye).toLocaleString(), "right")}
        ${td(Number(d.reste_a_payer).toLocaleString(), "right")}
      </tr>`).join("");

    const ligneRemises = remises.map((r, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(r.montant ? `${Number(r.montant).toLocaleString()} FCFA` : `${r.pourcentage}%`, "right")}
        ${td(esc(r.motif))}
        ${td(new Date(r.date_creation).toLocaleDateString(), "center")}
      </tr>`).join("");

    const html = `
      <!DOCTYPE html>
      <html>
        <head><meta charset="UTF-8"><title>Relevé - ${esc(situation.eleve)}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Times New Roman', serif; padding: 20px; background: white; color: #000; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .header { text-align: center; margin-bottom: 18px; border-bottom: 2px solid #004080; padding-bottom: 12px; }
            .header h1 { font-size: 20pt; color: #004080; text-transform: uppercase; margin-bottom: 6px; }
            .header .sous-titre { font-size: 13pt; font-weight: bold; color: #222; margin-bottom: 4px; }
            .header .meta { font-size: 10pt; color: #555; }
            h3.section { font-size: 13pt; color: #004080; margin: 18px 0 8px; border-left: 4px solid #004080; padding-left: 8px; }
            .kpis { font-size: 10.5pt; margin-bottom: 8px; }
            .kpis div { padding: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; margin-bottom: 4px; font-size: 10pt; }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
            thead tr { background: #004080 !important; color: white; }
            thead th { padding: 7px 8px; border: 1px solid #003366; text-align: center; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Relevé de situation</h1>
            <div class="sous-titre">${esc(situation.eleve)}</div>
            <div class="meta">${esc(situation.classe)} &nbsp;·&nbsp; Généré le ${dateGen}</div>
          </div>
          <div class="kpis">
            <div>Total dû : <strong>${Number(situation.total_du).toLocaleString()} FCFA</strong></div>
            <div>Remises : <strong>${Number(situation.total_remises).toLocaleString()} FCFA</strong></div>
            <div>Total payé : <strong>${Number(situation.total_paye).toLocaleString()} FCFA</strong></div>
            <div>Reste à payer : <strong>${Number(situation.total_reste).toLocaleString()} FCFA</strong></div>
            <div>Statut : <strong>${esc(statut.label)}</strong></div>
          </div>

          ${versements.length > 0 ? `
            <h3 class="section">Historique des versements</h3>
            <table>
              <thead><tr><th>Date</th><th>Reçu</th><th>Montant</th><th>Mode</th><th>Statut</th></tr></thead>
              <tbody>${ligneVersements}</tbody>
            </table>
          ` : ""}

          <h3 class="section">Détail par tranche</h3>
          <table>
            <thead><tr><th>Type de frais</th><th>Tranche</th><th>Échéance</th><th>Dû</th><th>Payé</th><th>Reste</th></tr></thead>
            <tbody>${ligneDetail}</tbody>
          </table>

          ${remises.length > 0 ? `
            <h3 class="section">Remises accordées</h3>
            <table>
              <thead><tr><th>Valeur</th><th>Motif</th><th>Date</th></tr></thead>
              <tbody>${ligneRemises}</tbody>
            </table>
          ` : ""}

          <script>
            window.onload = function() { setTimeout(function() { window.print(); }, 300); };
            window.onafterprint = function() { window.close(); };
          <\/script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=900,height=700,scrollbars=yes");
    if (!printWindow) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression (pop-up bloquée ?)");
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  };

  return (
    <div>
      <div className="sb2">
        <button className="bsec" onClick={onBack}><i className="ti ti-arrow-left" />Retour à la liste</button>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--t)" }}>{situation.eleve}</div>
            <div style={{ fontSize: 13, color: "var(--m)" }}>{situation.classe}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13, color: "var(--m)" }}>Scolarité :</span>
              <span className={`pill ${statut.pill}`} style={{ fontSize: 14 }}>{statut.label}</span>
            </div>
            {/* caisse.exporter_situation */}
            {peutExporter && (
              <>
                <button
                  className="bsec"
                  onClick={genererPDFReleve}
                  disabled={generatingPDF}
                  style={{ color: "#dc3545", borderColor: "#dc3545" }}
                >
                  <i className="ti ti-file-pdf" /> {generatingPDF ? "Génération..." : "PDF"}
                </button>
                <button className="bsec" onClick={imprimerReleve} style={{ color: "#6f42c1", borderColor: "#6f42c1" }}>
                  <i className="ti ti-printer" />Imprimer
                </button>
              </>
            )}
            {/* caisse.encaisser_versement */}
            {peutEncaisser && (
              <button className="bprim" onClick={() => setModal("versement")}><i className="ti ti-cash" />Nouveau versement</button>
            )}
            {/* caisse.gerer_remises */}
            {peutGererRemises && (
              <button className="bsec" onClick={() => setModal("remise")}><i className="ti ti-discount-2" />Nouvelle remise</button>
            )}
          </div>
        </div>

        <div className="sg" style={{ gridTemplateColumns: "repeat(4, minmax(0,1fr))" }}>
          <div className="sc sc-accent">
            <div className="sclbl">Total dû</div>
            <div className="scval" style={{ fontSize: 20 }}>{Number(situation.total_du).toLocaleString()}</div>
          </div>
          <div className="sc sc-accent-am">
            <div className="sclbl">Remises</div>
            <div className="scval" style={{ fontSize: 20 }}>{Number(situation.total_remises).toLocaleString()}</div>
          </div>
          <div className="sc sc-accent-gn">
            <div className="sclbl">Total payé</div>
            <div className="scval" style={{ fontSize: 20 }}>{Number(situation.total_paye).toLocaleString()}</div>
          </div>
          <div className="sc sc-accent-bl">
            <div className="sclbl">Reste à payer</div>
            <div className="scval" style={{ fontSize: 20 }}>{Number(situation.total_reste).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {versements.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: "auto", marginBottom: 14 }}>
          <div className="ch" style={{ padding: "12px 14px 0" }}><div className="ct">Historique des versements</div></div>
          <table className="tbl" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <th style={CELL_NOWRAP}>Date</th>
                <th style={CELL_NOWRAP}>Reçu</th>
                <th style={CELL_NOWRAP}>Montant</th>
                <th>Mode</th>
                <th style={CELL_NOWRAP}>Statut</th>
                <th style={CELL_NOWRAP}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {versements.map(v => (
                <VersementRow
                  key={v.id}
                  versement={v}
                  busy={busyVersementId === v.id}
                  peutAnnuler={peutAnnuler}
                  onImprimer={handleImprimerVersement}
                  onDemanderAnnulation={setVersementAnnulerCible}
                  onVoirMotif={setVersementMotifCible}
                />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="2" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Total encaissé</td>
                <td style={{ fontWeight: 700, color: "var(--pr)", whiteSpace: "nowrap" }}>{totalVersements.toLocaleString()} FCFA</td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflowX: "auto", marginBottom: 14 }}>
        <div className="ch" style={{ padding: "12px 14px 0" }}><div className="ct">Détail par tranche</div></div>
        <table className="tbl" style={{ minWidth: 620 }}>
          <thead>
            <tr>
              <th>Type de frais</th>
              <th>Tranche</th>
              <th style={CELL_NOWRAP}>Échéance</th>
              <th style={CELL_NOWRAP}>Dû</th>
              <th style={CELL_NOWRAP}>Payé</th>
              <th style={CELL_NOWRAP}>Reste</th>
            </tr>
          </thead>
          <tbody>
            {situation.detail.map(d => (
              <tr key={d.engagement_id}>
                <td style={CELL_TEXT}>{d.type_frais}</td>
                <td style={CELL_TEXT}>{d.tranche}</td>
                <td style={CELL_NOWRAP}>{d.date_echeance}</td>
                <td style={CELL_NOWRAP}>{Number(d.montant_du).toLocaleString()}</td>
                <td style={CELL_NOWRAP}>{Number(d.montant_paye).toLocaleString()}</td>
                <td style={{ ...CELL_NOWRAP, color: Number(d.reste_a_payer) > 0 ? "var(--rd)" : "var(--gn)", fontWeight: 600 }}>
                  {Number(d.reste_a_payer).toLocaleString()}
                </td>
              </tr>
            ))}
            {situation.detail.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun engagement de frais pour cet élève</td></tr>
            )}
          </tbody>
          {situation.detail.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan="3" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Total</td>
                <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{totalDetailDu.toLocaleString()}</td>
                <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{totalDetailPaye.toLocaleString()}</td>
                <td style={{ fontWeight: 700, whiteSpace: "nowrap", color: totalDetailReste > 0 ? "var(--rd)" : "var(--gn)" }}>
                  {totalDetailReste.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {remises.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <div className="ch" style={{ padding: "12px 14px 0" }}><div className="ct">Remises accordées</div></div>
          <table className="tbl" style={{ minWidth: 420 }}>
            <thead>
              <tr>
                <th style={CELL_NOWRAP}>Valeur</th>
                <th>Motif</th>
                <th style={CELL_NOWRAP}>Date</th>
              </tr>
            </thead>
            <tbody>
              {remises.map(r => (
                <tr key={r.id}>
                  <td style={CELL_NOWRAP}>{r.montant ? `${Number(r.montant).toLocaleString()} FCFA` : `${r.pourcentage}%`}</td>
                  <td style={{ ...CELL_TEXT, minWidth: 160 }}>{r.motif}</td>
                  <td style={CELL_NOWRAP}>{new Date(r.date_creation).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal === "versement" && peutEncaisser && (
        <VersementModal
          slug={slug}
          inscriptionId={inscriptionId}
          engagements={situation.detail.filter(d => Number(d.reste_a_payer) > 0)}
          onClose={() => setModal(null)}
          onCreated={handleVersementCree}
        />
      )}
      {modal === "remise" && peutGererRemises && (
        <RemiseModal slug={slug} inscriptionId={inscriptionId} onClose={() => setModal(null)} onCreated={() => { setModal(null); load(true); }} />
      )}

      {versementAnnulerCible && peutAnnuler && (
        <AnnulationModal
          saving={busyVersementId === versementAnnulerCible.id}
          onClose={() => setVersementAnnulerCible(null)}
          onConfirm={handleConfirmerAnnulation}
        />
      )}
      {versementMotifCible && (
        <Modal title="Motif d'annulation" onClose={() => setVersementMotifCible(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", whiteSpace: "pre-wrap" }}>
            {versementMotifCible.motif_annulation || "Aucun motif renseigné"}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setVersementMotifCible(null)}>Fermer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function VersementRow({ versement, busy, peutAnnuler, onImprimer, onDemanderAnnulation, onVoirMotif }) {
  return (
    <tr>
      <td style={CELL_NOWRAP}>{new Date(versement.date_versement).toLocaleDateString()}</td>
      <td style={{ ...CELL_NOWRAP, fontWeight: 500 }}>{versement.numero_recu}</td>
      <td style={CELL_NOWRAP}>{Number(versement.montant).toLocaleString()} FCFA</td>
      <td style={CELL_TEXT}>{versement.mode_paiement_libelle}</td>
      <td style={CELL_NOWRAP}><span className={`pill ${versement.statut === "VALIDE" ? "ok" : "err"}`}>{versement.statut}</span></td>
      <td>
        <div className="act">
          <ActionBtn title="Imprimer le reçu" icon="ti-printer" onClick={() => onImprimer(versement)} disabled={busy} bg="var(--pr)" />
          {/* caisse.annuler_versement */}
          {peutAnnuler && versement.statut === "VALIDE" && (
            <ActionBtn title="Annuler" icon="ti-x" onClick={() => onDemanderAnnulation(versement)} disabled={busy} bg="var(--rd)" />
          )}
          {versement.statut !== "VALIDE" && versement.motif_annulation && (
            <ActionBtn title="Voir le motif d'annulation" icon="ti-info-circle" onClick={() => onVoirMotif(versement)} bg="#6c757d" />
          )}
        </div>
      </td>
    </tr>
  );
}

function VersementModal({ slug, inscriptionId, engagements, onClose, onCreated }) {
  const [typesFrais, setTypesFrais] = useState([]);
  const [caisses, setCaisses] = useState([]);
  const [modesPaiement, setModesPaiement] = useState([]);

  // Deux façons de saisir le versement :
  // - mode simple (par défaut, le plus courant) : on est déjà sur la fiche
  //   de l'élève ; il suffit de choisir la catégorie de frais réglée et le
  //   montant. Le backend répartit lui-même en cascade sur les tranches de
  //   cette catégorie (cf. services.repartir_versement_sur_tranches).
  // - mode avancé ("Payer par tranche") : on choisit précisément la tranche
  //   à solder, ou on répartit sur tous les frais dus, tous types confondus.
  const [modeAvance, setModeAvance] = useState(false);
  const [modeRepartition, setModeRepartition] = useState("tranche"); // 'tranche' | 'tout_confondu' — mode avancé uniquement

  const [typeFrais, setTypeFrais] = useState("");
  const [deduireScolarite, setDeduireScolarite] = useState(false);
  const [caisse, setCaisse] = useState("");
  const [montant, setMontant] = useState("");
  const [modePaiement, setModePaiement] = useState("");
  const [engagement, setEngagement] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingRef, setLoadingRef] = useState(true);

  const toast = useToastHook();
  const { getTypesFrais, getCaisses, getModesPaiement, creerVersement } = useAdminService();

  useEffect(() => {
    Promise.all([getTypesFrais(slug), getCaisses(slug), getModesPaiement(slug)])
      .then(([tf, c, mp]) => { setTypesFrais(tf); setCaisses(c); setModesPaiement(mp); })
      .catch(() => toast.error("Impossible de charger les données de référence"))
      .finally(() => setLoadingRef(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const typeFraisSelectionne = typesFrais.find(tf => String(tf.id) === String(typeFrais));
  // Convention de l'établissement pour repérer le type de frais "Scolarité"
  // (cf. le placeholder "Ex: SCOL" utilisé dans Configuration > Types de frais).
  const typeFraisScolarite = typesFrais.find(tf => (tf.code || "").toUpperCase() === "SCOL");
  // La case à cocher « Déduire des frais de scolarité » est proposée dès
  // qu'un type de frais "Scolarité" existe dans l'établissement et que le
  // type sélectionné n'est pas lui-même la scolarité (elle n'a plus de sens
  // dans ce cas). On ne restreint plus à typeFraisSelectionne.deductible_scolarite :
  // c'est le caissier qui décide, au cas par cas, s'il veut imputer le
  // règlement sur la scolarité plutôt que sur le type de frais choisi.
  const peutDeduireScolarite = !!typeFrais && !!typeFraisScolarite && String(typeFrais) !== String(typeFraisScolarite.id);

  const basculerModeAvance = () => {
    setModeAvance(v => !v);
    setModeRepartition("tranche");
    setEngagement("");
    setDeduireScolarite(false);
  };

  const changerTypeFrais = (value) => {
    setTypeFrais(value);
    setEngagement("");
    if (typeFraisScolarite && String(value) === String(typeFraisScolarite.id)) setDeduireScolarite(false);
  };

  // Construit la cascade d'imputations sur les tranches de scolarité non
  // soldées de l'élève (même principe que la répartition automatique du
  // backend, mais ciblée sur un autre type de frais que celui choisi dans
  // le formulaire — c'est le mécanisme d'imputation inter-frais RG5, cf.
  // ImputationFrais / VersementCreateAPIView côté serveur).
  const construireImputationsScolarite = (montantAImputer) => {
    const tranchesScolarite = [...engagements]
      .filter(d => String(d.type_frais_id) === String(typeFraisScolarite.id))
      .sort((a, b) => new Date(a.date_echeance) - new Date(b.date_echeance));

    let restant = Number(montantAImputer);
    const imputations = [];
    for (const d of tranchesScolarite) {
      if (restant <= 0) break;
      const du = Number(d.reste_a_payer);
      const part = Math.min(du, restant);
      if (part > 0) {
        imputations.push({ engagement_id: d.engagement_id, montant: part });
        restant -= part;
      }
    }
    // Trop-perçu au-delà de la scolarité due : ajouté à la dernière tranche
    // imputée (même convention que le backend pour la répartition "tout confondu").
    if (restant > 0 && imputations.length > 0) {
      imputations[imputations.length - 1].montant += restant;
    }
    return imputations;
  };

  const handleSave = async () => {
    if (saving) return; // évite un double envoi (double clic)
    if (!modeAvance || modeRepartition !== "tout_confondu") {
      if (!typeFrais) { toast.error("Le type de frais est requis"); return; }
    }
    if (!caisse || !modePaiement) { toast.error("La caisse et le mode de paiement sont requis"); return; }
    if (!montant || Number(montant) <= 0) { toast.error("Le montant doit être supérieur à 0"); return; }

    if (!modeAvance && deduireScolarite && peutDeduireScolarite) {
      const tranchesScolarite = engagements.filter(d => String(d.type_frais_id) === String(typeFraisScolarite.id));
      if (tranchesScolarite.length === 0) {
        toast.error("Aucune tranche de scolarité due pour cet élève — impossible de déduire");
        return;
      }
    }

    const data = { caisse, montant, mode_paiement: modePaiement };
    if (!modeAvance || modeRepartition !== "tout_confondu") data.type_frais = typeFrais;
    if (modeAvance && modeRepartition === "tranche" && engagement) data.engagement = engagement;
    if (!modeAvance && deduireScolarite && peutDeduireScolarite) {
      data.imputations = construireImputationsScolarite(montant);
    }

    await enregistrer(data);
  };

  // Si la répartition automatique ne trouve aucune tranche du type de frais
  // choisi pour cet élève, le backend refuse par sécurité. On propose alors
  // au caissier d'encaisser quand même comme simple recette non liée à une
  // tranche (autoriser_hors_tranche).
  const enregistrer = async (data) => {
    setSaving(true);
    try {
      const versement = await creerVersement(slug, inscriptionId, data);
      toast.success(`Versement enregistré — reçu ${versement.numero_recu}`);
      onCreated(versement);
    } catch (error) {
      const message = error.response?.data?.error;
      const sansTrancheAuto =
        message?.startsWith("Aucune tranche de type «") && !data.engagement && !data.imputations;
      if (sansTrancheAuto) {
        await enregistrer({ ...data, autoriser_hors_tranche: true });
        return;
      }
      toast.error(message || "Impossible d'enregistrer ce versement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouveau versement" onClose={onClose}>
      {loadingRef ? <Loader /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>

          {!modeAvance && (
            <>
              <div className="fg">
                <label>Type de frais *</label>
                <select value={typeFrais} onChange={e => changerTypeFrais(e.target.value)}>
                  <option value="">-- Sélectionner --</option>
                  {typesFrais.map(tf => <option key={tf.id} value={tf.id}>{tf.libelle}</option>)}
                </select>
              </div>

              {peutDeduireScolarite && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={deduireScolarite} onChange={e => setDeduireScolarite(e.target.checked)} />
                  Déduire des frais de scolarité
                </label>
              )}
              {peutDeduireScolarite && deduireScolarite && (
                <div style={{ fontSize: 12, color: "var(--h)", marginTop: -4 }}>
                  Le montant réglé viendra réduire le solde de scolarité de l'élève,
                  plutôt que le solde de « {typeFraisSelectionne?.libelle} ».
                </div>
              )}
            </>
          )}

          {modeAvance && (
            <div className="fg">
              <label>Répartition *</label>
              <select value={modeRepartition} onChange={e => { setModeRepartition(e.target.value); setEngagement(""); }}>
                <option value="tranche">Une tranche précise</option>
                <option value="tout_confondu">Tout confondu — répartir sur tous les frais dus</option>
              </select>
              {modeRepartition === "tout_confondu" && (
                <div style={{ fontSize: 12, color: "var(--h)", marginTop: 4 }}>
                  Le montant sera réparti automatiquement sur toutes les tranches non
                  soldées de l'élève (scolarité, fournitures, tenue...), par ordre
                  d'échéance. À utiliser quand le parent règle tout d'un coup sans
                  distinguer les catégories.
                </div>
              )}
            </div>
          )}

          {modeAvance && modeRepartition !== "tout_confondu" && (
            <div className="fg">
              <label>Type de frais *</label>
              <select value={typeFrais} onChange={e => changerTypeFrais(e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {typesFrais.map(tf => <option key={tf.id} value={tf.id}>{tf.libelle}</option>)}
              </select>
            </div>
          )}

          {modeAvance && modeRepartition === "tranche" && (
            <div className="fg">
              <label>Tranche concernée *</label>
              <select value={engagement} onChange={e => setEngagement(e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {engagements
                  .filter(d => !typeFrais || String(d.type_frais_id) === String(typeFrais))
                  .map(d => (
                    <option key={d.engagement_id} value={d.engagement_id}>
                      {d.type_frais} — {d.tranche} (reste {Number(d.reste_a_payer).toLocaleString()} FCFA)
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div className="fgrid">
            <div className="fg">
              <label>Caisse *</label>
              <select value={caisse} onChange={e => setCaisse(e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {caisses.filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Mode de paiement *</label>
              <select value={modePaiement} onChange={e => setModePaiement(e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {modesPaiement.filter(m => m.actif).map(m => <option key={m.id} value={m.id}>{m.libelle}</option>)}
              </select>
            </div>
          </div>
          <div className="fg">
            <label>Montant (FCFA) *</label>
            <input type="number" min="0.01" step="0.01" value={montant} onChange={e => setMontant(e.target.value)} />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="button" className="bsec" style={{ padding: "4px 10px", fontSize: 13 }} onClick={basculerModeAvance}>
              {modeAvance
                ? <><i className="ti ti-arrow-left" /> Retour au versement simple</>
                : <><i className="ti ti-list-numbers" /> Payer par tranche</>}
            </button>
          </div>
        </div>
      )}
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="bprim" onClick={handleSave} disabled={saving || loadingRef}>
          {saving ? "En cours..." : <><i className="ti ti-check" /> Encaisser</>}
        </button>
      </div>
    </Modal>
  );
}

function RemiseModal({ slug, inscriptionId, onClose, onCreated }) {
  const [mode, setMode] = useState("montant"); // 'montant' | 'pourcentage'
  const [valeur, setValeur] = useState("");
  const [motif, setMotif] = useState("");
  const [saving, setSaving] = useState(false);

  const toast = useToastHook();
  const { creerRemiseIndividuelle } = useAdminService();

  const handleSave = async () => {
    if (saving) return; // évite un double envoi (double clic)
    if (!valeur || Number(valeur) <= 0) { toast.error("Renseignez une valeur de remise valide"); return; }
    if (!motif.trim()) { toast.error("Le motif est requis"); return; }
    setSaving(true);
    try {
      const data = { motif };
      if (mode === "montant") data.montant = valeur; else data.pourcentage = valeur;
      await creerRemiseIndividuelle(slug, inscriptionId, data);
      toast.success("Remise créée avec succès");
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.non_field_errors?.[0] || "Impossible de créer cette remise");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouvelle remise individuelle" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Type de remise *</label>
          <select value={mode} onChange={e => { setMode(e.target.value); setValeur(""); }}>
            <option value="montant">Montant fixe (FCFA)</option>
            <option value="pourcentage">Pourcentage (%)</option>
          </select>
        </div>
        <div className="fg">
          <label>{mode === "montant" ? "Montant (FCFA) *" : "Pourcentage (%) *"}</label>
          <input type="number" min="0" step="0.01" max={mode === "pourcentage" ? 100 : undefined} value={valeur} onChange={e => setValeur(e.target.value)} />
        </div>
        <div className="fg">
          <label>Motif *</label>
          <textarea value={motif} onChange={e => setMotif(e.target.value)} placeholder="Ex: Bourse d'excellence, fratrie, situation sociale..." />
        </div>
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="bprim" onClick={handleSave} disabled={saving}>{saving ? "..." : <><i className="ti ti-check" /> Créer</>}</button>
      </div>
    </Modal>
  );
}